/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import AdminCoach from 'bundles/admin-dashboard/components/AdminCoach';
import { MoreVerticalFilledIcon } from 'bundles/ai-course-coach/assets/mui';
import { useIsAdminCoachV2Enabled } from 'bundles/enterprise-admin-hooks/hooks/useFeatures';
import { AdminAuthNavigation } from 'bundles/enterprise-admin-navigation/components/AdminAuthNavigation';
import { EnterpriseAdminCoachProvider } from 'bundles/enterprise-admin/EnterpriseAdminCoachContext';
import { useEnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

const styles = {
  resizeHandle: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
    display: flex;
    justify-content: center;
    align-items: center;
    border-left: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    color: var(--cds-color-grey-700);
    transition:
      color 0.2s,
      background-color 0.2s;

    :hover,
    :focus {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  coach: css`
    padding-left: 0;
    ${breakpoints.up('sm')} {
      z-index: auto;
    }
  `,
  fixedCoach: css`
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  `,
};

const WithGlobalNavAndStyleGuideContent: React.LegacyFunctionComponentWithChildren<{}> = ({ children, ...props }) => {
  const org = useThirdPartyOrganization();
  const { adminRoleType } = useEnterpriseAdminContext();
  const [showCoach, setShowCoach] = React.useState(false);
  const isDesktop = useMediaQuery(breakpoints.up('sm'));
  const toggleCoach = () => {
    setShowCoach((prev) => !prev);
  };

  const isAdminCoachV2Enabled = useIsAdminCoachV2Enabled();

  return (
    <div className="styleguide">
      <AdminAuthNavigation
        thirdPartyOrganization={org}
        adminRoleType={adminRoleType}
        showCoach={showCoach}
        toggleCoach={toggleCoach}
        isAdminCoachV2Enabled={isAdminCoachV2Enabled}
      />
      {/* Had to use inline style for PanelGroup and Panel as emotion css didn't work as expected. */}
      {/* AdminAuthNavigation height is reduced from the viewport height */}
      {/* Explicit IDs added for SSR hydration stability - prevents "No group found for id" errors */}
      <PanelGroup
        id="admin-dashboard-panel-group"
        direction="horizontal"
        style={showCoach && isDesktop ? { height: 'calc(100vh - 66px)' } : {}}
      >
        <Panel
          id="admin-dashboard-main-panel"
          style={showCoach && isDesktop ? { overflow: 'auto', height: '100%' } : {}}
        >
          {React.isValidElement(children) ? React.cloneElement(children, props) : children}
        </Panel>

        {showCoach && isDesktop && (
          <>
            <PanelResizeHandle id="admin-dashboard-resize-handle" css={styles.resizeHandle}>
              <MoreVerticalFilledIcon size={16} />
            </PanelResizeHandle>
            <Panel
              id="admin-dashboard-coach-panel"
              minSize={25}
              defaultSize={28}
              maxSize={50}
              style={{
                position: 'relative',
                backgroundColor: 'var(--cds-color-neutral-background-primary-weak)',
              }}
            >
              <AdminCoach thirdPartyOrganization={org} toggleCoach={toggleCoach} style={styles.coach} />
            </Panel>
          </>
        )}
      </PanelGroup>

      {/* Mobile coach rendered outside PanelGroup - only Panel/PanelResizeHandle can be direct children */}
      {showCoach && !isDesktop && (
        <AdminCoach thirdPartyOrganization={org} toggleCoach={toggleCoach} style={styles.fixedCoach} />
      )}
    </div>
  );
};

const WithGlobalNavAndStyleGuide: React.LegacyFunctionComponentWithChildren<{}> = ({ children, ...props }) => {
  return (
    <EnterpriseAdminCoachProvider>
      <WithGlobalNavAndStyleGuideContent {...props}>{children}</WithGlobalNavAndStyleGuideContent>
    </EnterpriseAdminCoachProvider>
  );
};

export default WithGlobalNavAndStyleGuide;
